// Site header behaviour: click-to-open mega-menu flyouts, expanding search,
// mobile burger menu, and sticky-on-scroll. Vanilla JS — markup in
// partial/navigation.twig, styles in src/sass/partials/_header.scss.
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade'

export default class Menu {
  constructor() {
    this.header = document.querySelector('.header')
    this.flyoutTriggers = Array.from(document.querySelectorAll('[data-flyout-trigger]'))
    this.searchToggles = Array.from(document.querySelectorAll('.search-toggle'))
    this.searchPanel = document.getElementById('header-search')
    this.headerInner = document.querySelector('.site-header__inner')
    this.burger = document.getElementById('header-burger')
    this.mobileMenu = document.getElementById('mobile-menu')

    this.initFlyouts()
    this.initSearch()
    this.initBurger()
    this.initStickyScroll()

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll()
    })
    document.addEventListener('click', (e) => {
      if (this.header && !this.header.contains(e.target)) {
        this.closeFlyouts()
        this.closeSearch()
      }
    })
  }

  // ── Mega-menu flyouts ──────────────────────────────────────
  initFlyouts() {
    this.flyoutTriggers.forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'))
      if (!panel) return
      trigger.addEventListener('click', (e) => {
        e.stopPropagation()
        const isOpen = panel.classList.contains('is-open')
        this.closeFlyouts()
        this.closeSearch()
        if (!isOpen) {
          panel.classList.add('is-open')
          trigger.setAttribute('aria-expanded', 'true')
        }
      })
    })
  }

  closeFlyouts() {
    this.flyoutTriggers.forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'))
      if (panel) panel.classList.remove('is-open')
      trigger.setAttribute('aria-expanded', 'false')
    })
  }

  // ── Expanding search ───────────────────────────────────────
  initSearch() {
    if (!this.searchPanel) return
    this.searchToggles.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        if (this.searchPanel.classList.contains('is-open')) {
          this.closeSearch()
        } else {
          this.closeFlyouts()
          this.openSearch()
        }
      })
    })
  }

  openSearch() {
    // The mobile menu sheet sits above the search bar and locks body scroll —
    // dismiss it so the search field is actually visible and usable.
    this.closeMobile()
    this.searchPanel.hidden = false
    this.searchPanel.classList.add('is-open')
    if (this.headerInner) this.headerInner.classList.add('is-searching')
    const input = this.searchPanel.querySelector('input[type="search"]')
    if (input) input.focus()
    this.syncSearchToggles('true')
  }

  closeSearch() {
    if (!this.searchPanel) return
    this.searchPanel.classList.remove('is-open')
    this.searchPanel.hidden = true
    if (this.headerInner) this.headerInner.classList.remove('is-searching')
    this.syncSearchToggles('false')
  }

  syncSearchToggles(state) {
    this.searchToggles.forEach((btn) => {
      if (btn.hasAttribute('aria-controls')) btn.setAttribute('aria-expanded', state)
    })
  }

  // ── Mobile burger menu ─────────────────────────────────────
  initBurger() {
    if (!this.burger || !this.mobileMenu) return
    this.burger.addEventListener('click', (e) => {
      e.stopPropagation()
      if (this.mobileMenu.classList.contains('is-open')) {
        this.closeMobile()
      } else {
        this.openMobile()
      }
    })
  }

  openMobile() {
    // Menu and search are mutually exclusive — see openSearch().
    this.closeSearch()
    // The panel is a fixed, scrollable sheet that starts just below the header
    // bar. Measure the bar first (menu still hidden, so header height == bar).
    if (this.header) {
      this.mobileMenu.style.top = `${this.header.getBoundingClientRect().bottom}px`
    }
    this.mobileMenu.hidden = false
    this.mobileMenu.classList.add('is-open')
    this.burger.setAttribute('aria-expanded', 'true')
    // Lock the background but let the panel itself scroll (fixes iOS scroll-through).
    // The lock fixes the body in place, which kills the header's sticky
    // positioning — pin the header for the duration of the lock.
    if (this.header) this.header.classList.add('is-locked')
    disableBodyScroll(this.mobileMenu, { reserveScrollBarGap: true })
  }

  closeMobile() {
    if (!this.mobileMenu) return
    this.mobileMenu.classList.remove('is-open')
    this.mobileMenu.hidden = true
    enableBodyScroll(this.mobileMenu)
    if (this.header) this.header.classList.remove('is-locked')
    if (this.burger) this.burger.setAttribute('aria-expanded', 'false')
  }

  closeAll() {
    this.closeFlyouts()
    this.closeSearch()
    this.closeMobile()
  }

  // ── On-scroll state (hides the top strip once you scroll past it) ─
  initStickyScroll() {
    const topStrip = document.querySelector('.top-strip')
    const onScroll = () => {
      const scrolled = window.pageYOffset || document.documentElement.scrollTop
      const threshold = topStrip ? topStrip.offsetHeight : 0
      if (topStrip) topStrip.classList.toggle('hide', scrolled > threshold)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
  }
}
